import { AUDIO_ERROR_CODES, CHAT_ERROR_CODES } from '@shared/constants/error-codes';
import {
  AUDIO_ATTACHMENT_EXTENSIONS,
  AUDIO_ATTACHMENT_MIME_TYPES,
  AUDIO_TRANSCRIPTION_LIMITS,
} from '@shared/constants/audio-transcription';
import type { AudioAttachmentMime } from '@shared/schemas/audio-transcription';

export const CHAT_DOCUMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Single source of truth for the document formats the chat composer accepts: the
 * file picker (`accept` attribute), the drag-and-drop filter and the upload all derive
 * from it. It mirrors the backend chat attachment allow-list (PDF, DOCX, XLSX and the
 * V1 3D formats; PPTX is RAG-only), so an unsupported file is refused client-side.
 */
const CHAT_DOCUMENT_TYPES = [
  { extension: '.pdf', mime: 'application/pdf' },
  { extension: '.docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
  { extension: '.xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  { extension: '.glb', mime: 'model/gltf-binary' },
  { extension: '.gltf', mime: 'model/gltf+json' },
  { extension: '.obj', mime: 'model/obj' },
  { extension: '.stl', mime: 'model/stl' },
  { extension: '.ply', mime: 'model/ply' },
  { extension: '.3mf', mime: 'model/3mf' },
] as const;

const AUDIO_EXTENSION_TO_MIME = {
  '.aac': 'audio/aac',
  '.flac': 'audio/flac',
  '.m4a': 'audio/mp4',
  '.mp3': 'audio/mpeg',
  '.ogg': 'audio/ogg',
  '.opus': 'audio/opus',
  '.wav': 'audio/wav',
  '.webm': 'audio/webm',
} as const satisfies Record<(typeof AUDIO_ATTACHMENT_EXTENSIONS)[number], AudioAttachmentMime>;

export type ChatAttachmentKind = 'audio' | 'document';

export type SelectedChatAttachment = {
  file: File;
  kind: ChatAttachmentKind;
  mediaType: string;
};

export type ChatAttachmentSelectionResult = {
  accepted: SelectedChatAttachment[];
  rejected: Array<{ code: string; filename: string }>;
};

function getExtension(filename: string) {
  const match = /\.[^.]+$/u.exec(filename.trim().toLowerCase());
  return match?.[0] ?? '';
}

export function isAudioAttachmentMediaType(mediaType: string) {
  return (AUDIO_ATTACHMENT_MIME_TYPES as readonly string[]).includes(mediaType);
}

export function isDocumentAttachmentMediaType(mediaType: string) {
  return CHAT_DOCUMENT_TYPES.some((type) => type.mime === mediaType);
}

/**
 * Same rule as the picker's `accept` attribute: the MIME type OR the extension matches.
 * A dragged file may carry an empty or generic MIME depending on the OS, hence the
 * extension fallback. The server still validates the real content (magic bytes).
 */
export function resolveChatDocumentMediaType(file: File): string | null {
  if (isDocumentAttachmentMediaType(file.type)) {
    return file.type;
  }

  const extension = getExtension(file.name);
  return CHAT_DOCUMENT_TYPES.find((type) => type.extension === extension)?.mime ?? null;
}

export function resolveAudioMediaType(file: File): AudioAttachmentMime | null {
  if (isAudioAttachmentMediaType(file.type)) {
    return file.type as AudioAttachmentMime;
  }

  const extension = getExtension(file.name);
  if (extension in AUDIO_EXTENSION_TO_MIME) {
    return AUDIO_EXTENSION_TO_MIME[extension as keyof typeof AUDIO_EXTENSION_TO_MIME];
  }

  return null;
}

export function isAudioChatFile(file: File) {
  return resolveAudioMediaType(file) !== null;
}

export function isDocumentChatFile(file: File) {
  return resolveChatDocumentMediaType(file) !== null;
}

export function getChatComposerAcceptAttribute(audioEnabled = false) {
  const documentAccept = [
    ...CHAT_DOCUMENT_TYPES.map((type) => type.extension),
    ...CHAT_DOCUMENT_TYPES.map((type) => type.mime),
  ];

  if (!audioEnabled) {
    return documentAccept.join(',');
  }

  return [
    ...documentAccept,
    ...AUDIO_ATTACHMENT_MIME_TYPES,
    ...AUDIO_ATTACHMENT_EXTENSIONS,
  ].join(',');
}

export function selectMixedChatAttachments(input: {
  audioEnabled?: boolean;
  existingAudioCount: number;
  existingDocumentCount: number;
  files: readonly File[];
}): ChatAttachmentSelectionResult {
  const accepted: SelectedChatAttachment[] = [];
  const rejected: Array<{ code: string; filename: string }> = [];
  let audioCount = input.existingAudioCount;
  let documentCount = input.existingDocumentCount;
  let totalCount = input.existingAudioCount + input.existingDocumentCount;

  for (const file of input.files) {
    const audioMediaType = resolveAudioMediaType(file);

    if (audioMediaType) {
      if (input.audioEnabled !== true) {
        rejected.push({ code: AUDIO_ERROR_CODES.AUDIO_TRANSCRIPTION_DISABLED, filename: file.name });
        continue;
      }
      if (audioCount >= AUDIO_TRANSCRIPTION_LIMITS.maxFilesPerMessage) {
        rejected.push({ code: AUDIO_ERROR_CODES.AUDIO_ATTACHMENT_TOO_MANY, filename: file.name });
        continue;
      }
      if (totalCount >= AUDIO_TRANSCRIPTION_LIMITS.maxTotalAttachmentsPerMessage) {
        rejected.push({ code: CHAT_ERROR_CODES.CHAT_ATTACHMENT_TOO_MANY, filename: file.name });
        continue;
      }
      if (file.size > AUDIO_TRANSCRIPTION_LIMITS.maxSizeBytes) {
        rejected.push({ code: AUDIO_ERROR_CODES.AUDIO_ATTACHMENT_TOO_LARGE, filename: file.name });
        continue;
      }

      accepted.push({ file, kind: 'audio', mediaType: audioMediaType });
      audioCount += 1;
      totalCount += 1;
      continue;
    }

    const documentMediaType = resolveChatDocumentMediaType(file);

    if (documentMediaType) {
      if (documentCount >= AUDIO_TRANSCRIPTION_LIMITS.maxDocumentFilesPerMessage) {
        rejected.push({ code: CHAT_ERROR_CODES.CHAT_ATTACHMENT_TOO_MANY, filename: file.name });
        continue;
      }
      if (totalCount >= AUDIO_TRANSCRIPTION_LIMITS.maxTotalAttachmentsPerMessage) {
        rejected.push({ code: CHAT_ERROR_CODES.CHAT_ATTACHMENT_TOO_MANY, filename: file.name });
        continue;
      }
      if (file.size > CHAT_DOCUMENT_MAX_SIZE_BYTES) {
        rejected.push({ code: CHAT_ERROR_CODES.CHAT_ATTACHMENT_TOO_LARGE, filename: file.name });
        continue;
      }

      accepted.push({
        // The server compares the declared MIME with the detected content: upload the
        // canonical type, whatever empty/generic one the OS attached to the file.
        file: file.type === documentMediaType
          ? file
          : new File([file], file.name, { lastModified: file.lastModified, type: documentMediaType }),
        kind: 'document',
        mediaType: documentMediaType,
      });
      documentCount += 1;
      totalCount += 1;
      continue;
    }

    rejected.push({
      code: isAudioChatFile(file)
        ? AUDIO_ERROR_CODES.AUDIO_ATTACHMENT_UNSUPPORTED
        : CHAT_ERROR_CODES.CHAT_ATTACHMENT_UNSUPPORTED,
      filename: file.name,
    });
  }

  return { accepted, rejected };
}
